import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { TrendingUp, ExternalLink, Send, Clapperboard, Wallet, Settings, LogOut, Menu, Bell, Search, Moon, Sun } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { NEXUS_URL, fmtData } from "@/lib/helpers";
import { ConnectionNotice } from "@/components/ConnectionNotice";

const NAV = [
  { to: "/producao", icon: Clapperboard, label: "Produção", id: "producao", num: "01" },
  // Calendário editorial e programação de posts ficam no Nexus.
  { href: NEXUS_URL, icon: Send, label: "Nexus", id: "nexus", num: "02" },
  { to: "/comercial", icon: TrendingUp, label: "Comercial", id: "comercial", num: "03" },
  { to: "/financeiro", icon: Wallet, label: "Financeiro", id: "financeiro", num: "04" },
  { to: "/configuracoes", icon: Settings, label: "Configurações", id: "configuracoes", num: "05" },
];

const BOTTOM = ["producao", "nexus", "comercial", "financeiro", "configuracoes"];
const MOBILE_LABEL = { producao: "Produção", configuracoes: "Ajustes" };

// Itens externos abrem em outra aba; os internos usam o roteador.
function NavItem({ item, className, children, ...props }) {
  if (item.href) {
    const cls = typeof className === "function" ? className({ isActive: false }) : className;
    return <a href={item.href} target="_blank" rel="noopener noreferrer" className={cls} {...props}>{children}</a>;
  }
  return <NavLink to={item.to} end={item.to === "/"} className={className} {...props}>{children}</NavLink>;
}

export const LOGO_ROUND_URL = "/assets/netzer-logo-round.png";

function Logo() {
  return (
    <div className="flex items-center gap-2.5" data-testid="app-logo">
      <img src={LOGO_ROUND_URL} alt="Netzer Studio" className="h-9 w-9 rounded-full object-cover shrink-0" />
      <div className="leading-none">
        <p className="font-serif text-base text-[#F4F4F0] uppercase">Netzer</p>
        <p className="font-mono text-[9px] text-[#71717A] tracking-[0.3em] uppercase mt-0.5">Studio // OS</p>
      </div>
    </div>
  );
}

function NotifBell() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const load = () => api.get("/notificacoes").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notificacoes-button" className="relative p-2 text-[#71717A] hover:text-[#8CC63F] transition-colors">
          <Bell size={17} strokeWidth={1.8} />
          {items.length > 0 && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#F43F5E] text-[9px] font-mono text-white flex items-center justify-center" data-testid="notificacoes-count">{items.length}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#18181C] border-[#27272A] text-[#E4E4E0] w-80">
        {items.length === 0 && <p className="text-xs text-[#71717A] px-3 py-4 text-center">Tudo em dia por aqui.</p>}
        {items.map((n, i) => (
          <DropdownMenuItem key={i} data-testid={`notificacao-${n.tipo}`} onClick={() => navigate(n.ref)} className="cursor-pointer focus:bg-[#8CC63F]/10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mr-2 shrink-0" />
            <span className="text-xs leading-snug">{n.texto}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ThemeToggle() {
  const { mode, toggleMode } = useTheme();
  const isDark = mode === "dark";
  return (
    <button
      type="button"
      data-testid="theme-quick-toggle"
      onClick={toggleMode}
      className="p-2 rounded-lg text-[#71717A] hover:text-[#8CC63F] hover:bg-[#18181C] transition-colors"
      aria-label={isDark ? "Ativar modo claro" : "Ativar modo escuro"}
      title={isDark ? "Ativar modo claro" : "Ativar modo escuro"}
    >
      {isDark ? <Sun size={17} strokeWidth={1.8} /> : <Moon size={17} strokeWidth={1.8} />}
    </button>
  );
}

function BuscaGlobal() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    const handler = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setOpen(true); } };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.length < 2) { setRes(null); return; }
    timer.current = setTimeout(() => api.get(`/busca?q=${encodeURIComponent(q)}`).then((r) => setRes(r.data)).catch(() => {}), 250);
  }, [q]);

  const ir = (path) => { setOpen(false); setQ(""); setRes(null); navigate(path); };

  const Secao = ({ titulo, items, render, to }) =>
    items?.length > 0 && (
      <div className="mb-3">
        <p className="caption-label px-3 mb-1">{titulo}</p>
        {items.map((it) => (
          <button key={it.id} data-testid={`busca-${titulo}-${it.id}`} onClick={() => ir(to(it))} className="w-full text-left px-3 py-2 rounded-lg text-sm text-[#E4E4E0] hover:bg-[#8CC63F]/10 transition-colors">{render(it)}</button>
        ))}
      </div>
    );

  return (
    <>
      <button data-testid="busca-global-button" onClick={() => setOpen(true)} className="w-full flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm text-[#71717A] border border-[#27272A] hover:border-[#3F3F46] transition-colors">
        <Search size={15} /> Buscar… <span className="ml-auto font-mono text-[10px] border border-[#27272A] rounded px-1.5 py-0.5">⌘K</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-lg p-0 overflow-hidden">
          <DialogTitle className="sr-only">Busca global</DialogTitle>
          <div className="p-3 border-b border-[#27272A]">
            <Input data-testid="busca-global-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cliente, ciclo, tarefa, oportunidade…" className="bg-[#18181C] border-[#27272A]" />
          </div>
          <div className="max-h-80 overflow-y-auto p-2">
            {res && (
              <>
                <Secao titulo="clientes" items={res.clientes} render={(c) => c.empresa} to={(c) => `/producao/clientes/${c.id}`} />
                <Secao titulo="ciclos" items={res.ciclos} render={(c) => c.titulo} to={(c) => `/producao?view=pipeline&ciclo=${c.id}`} />
                <Secao titulo="tarefas" items={res.tarefas} render={(t) => t.titulo} to={(t) => t.ciclo_id ? `/producao?view=pipeline&ciclo=${t.ciclo_id}` : "/producao?view=hoje"} />
                <Secao titulo="oportunidades" items={res.oportunidades} render={(o) => `${o.empresa} — ${o.titulo}`} to={() => "/comercial"} />
                {res.clientes.length + res.ciclos.length + res.tarefas.length + (res.oportunidades?.length || 0) === 0 && (
                  <p className="text-xs text-[#71717A] text-center py-6">Nada encontrado.</p>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const fichaCliente = /^\/(?:producao\/)?clientes\/[^/]+\/?$/.test(pathname);
  const [open, setOpen] = useState(false);

  const podeVerFin = user?.role === "admin" || user?.fin_access;
  const items = NAV.filter((n) => (n.id !== "financeiro" && n.id !== "comercial") || podeVerFin);
  const bottomItems = items.filter((n) => BOTTOM.includes(n.id));

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const linkCls = ({ isActive }) =>
    `group flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors duration-150 ${
      isActive ? "bg-[#8CC63F]/10 text-[#8CC63F] border border-[#8CC63F]/25" : "text-[#A1A1AA] hover:text-[#F4F4F0] hover:bg-[#18181C] border border-transparent"
    }`;

  return (
    <div className="min-h-screen bg-[#09090B] film-grain">
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#0e0e11] border-r border-[#27272A] z-40">
        <div className="px-6 py-6 border-b border-[#27272A] flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <NotifBell />
          </div>
        </div>
        {!fichaCliente && <div className="px-3 pt-4"><BuscaGlobal /></div>}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map((n) => (
            <NavItem key={n.id} item={n} className={linkCls} data-testid={`sidebar-nav-${n.id}`}>
              <n.icon size={17} strokeWidth={1.8} />
              <span className="flex-1">{n.label}</span>
              {n.href ? <ExternalLink size={13} className="text-[#3f3f46] group-hover:text-[#8CC63F]" aria-label="abre em outra aba" /> : <span className="font-mono text-[10px] text-[#3f3f46] group-hover:text-[#8CC63F]">{n.num}</span>}
            </NavItem>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-[#27272A] flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#8CC63F]/15 border border-[#8CC63F]/30 flex items-center justify-center font-serif text-[#8CC63F] text-sm">
            {user?.name?.[0] || "N"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#E4E4E0] truncate">{user?.name}</p>
            <p className="caption-label">estúdio ativo</p>
          </div>
          <button onClick={doLogout} data-testid="logout-button" className="text-[#71717A] hover:text-[#F43F5E] transition-colors" title="Sair">
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      <header className="md:hidden sticky top-0 z-40 bg-[#09090B]/85 backdrop-blur-xl border-b border-[#27272A] px-4 py-3 flex items-center justify-between" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <Logo />
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <NotifBell />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-button" className="p-2 text-[#A1A1AA] hover:text-[#8CC63F]"><Menu size={20} /></button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0e0e11] border-l border-[#27272A] w-[min(88vw,20rem)]">
              <SheetHeader>
                <SheetTitle className="font-serif text-[#F4F4F0]">Menu do <span className="gold-italic">Estúdio</span></SheetTitle>
                <SheetDescription className="sr-only">Acesse as áreas principais do sistema.</SheetDescription>
              </SheetHeader>
              <nav className="mt-6 space-y-1">
                {items.map((n) => (
                  <NavItem key={n.id} item={n} onClick={() => setOpen(false)} className={linkCls} data-testid={`drawer-nav-${n.id}`}>
                    <n.icon size={17} strokeWidth={1.8} />
                    <span className="flex-1">{n.label}</span>
                    {n.href && <ExternalLink size={13} aria-label="abre em outra aba" />}
                  </NavItem>
                ))}
                <button onClick={doLogout} data-testid="drawer-logout-button" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-[#F43F5E]/80 hover:bg-[#F43F5E]/10">
                  <LogOut size={17} /> Sair
                </button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="md:pl-64">
        <div className="px-4 py-5 pb-28 md:px-8 md:py-8 lg:px-10 max-w-[1600px] mx-auto">
          <ConnectionNotice />
          <Outlet />
        </div>
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0e0e11]/90 backdrop-blur-xl border-t border-[#27272A]" aria-label="Navegação principal">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${bottomItems.length}, minmax(0, 1fr))`, paddingBottom: "env(safe-area-inset-bottom)" }}>
          {bottomItems.map((n) => (
            <NavItem key={n.id} item={n} data-testid={`bottom-nav-${n.id}`}
              className={({ isActive }) => `flex min-h-16 flex-col items-center justify-center gap-1 px-1 py-2 text-[10px] font-mono uppercase tracking-normal transition-colors ${isActive ? "text-[#8CC63F]" : "text-[#71717A]"}`}>
              <n.icon size={19} strokeWidth={1.8} />
              <span className="max-w-full truncate">{MOBILE_LABEL[n.id] || n.label}</span>
            </NavItem>
          ))}
        </div>
      </nav>
    </div>
  );
}
